import { CustomDataGrid, StackedTextCell } from '@retail/components';
import { useRowColorGrouping } from '@retail/hooks';
import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { IconButton, Stack, Typography } from '@mui/material';
import { East } from '@mui/icons-material';
import { PriceControlSupplier } from '@retail/price-control/types';
import { getLLDate } from '@retail/utils';

const baseColumnProps: Partial<GridColDef> = {
  width: 180,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};
interface Props {
  suppliersPriceControl: PriceControlSupplier[];
  isLoadingSuppliers: boolean;
  onSupplierClick: (supplierNumber: number) => void;
}

export function PriceControlSuppliersTable({
  suppliersPriceControl,
  isLoadingSuppliers,
  onSupplierClick,
}: Props) {
  const { getRowColorForStripedTable } = useRowColorGrouping();
  const t = useMyAssortmentTFunction();

  const columns: GridColDef<PriceControlSupplier>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'supplier',
        width: 240,
        headerName: t('myAssortment.columns.supplier'),
        renderCell: ({ row }) => <StackedTextCell topLine={row.name} bottomLine={row.number} />,
        valueGetter: (_, row) => row.name,
      },
      {
        ...baseColumnProps,
        field: 'itemsCount',
        headerName: t('myAssortment.columns.nrOfItems'),
        headerAlign: 'center',
        align: 'right',
        width: 120,
        renderCell: ({ row }) => (
          <Stack gap={0} alignItems="flex-end" pr={2.5}>
            <Stack flexDirection="row" alignItems="center" gap={0.5}>
              <Typography variant="body1" color="textPrimary" fontWeight={600}>
                {row.priceControlAmount}
              </Typography>
              <Typography variant="body2">/</Typography>
            </Stack>
            <Typography variant="body2">{row.totalItemsInAssortment}</Typography>
          </Stack>
        ),
      },
      {
        ...baseColumnProps,
        field: 'purchasePriceChange',
        headerName: t('myAssortment.priceControl.averagePurchasePriceChange'),
        renderCell: ({ row }) => (
          <StackedTextCell
            topLine={`${row.purchasePriceAverageDiffPercentage ?? '-'} %`}
            bottomLine={`${row.purchasePriceAverageDiffAbsolute ?? '-'} ${t(
              'myAssortment.columns.freightCondition.fixedAddOn'
            )}`}
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'salesPriceChange',
        headerName: t('myAssortment.priceControl.averageSalesPriceChange'),
        renderCell: ({ row }) => (
          <StackedTextCell
            topLine={`${row.salesPriceAverageDiffPercentage ?? '-'} %`}
            bottomLine={`${row.salesPriceAverageDiffAbsolute ?? '-'} ${t(
              'myAssortment.columns.freightCondition.fixedAddOn'
            )}`}
          />
        ),
      },

      {
        ...baseColumnProps,
        field: 'dgChange',
        width: 200,
        renderHeader: () => (
          <StackedTextCell
            topLine={t('myAssortment.priceControl.averageFactorChange')}
            bottomLine={t('myAssortment.priceControl.highestFactorChange')}
          />
        ),
        renderCell: ({ row }) => {
          return (
            <StackedTextCell
              topLine={`${row.salesFactorAverageDiffPercentage ?? '-'} %`}
              bottomLine={`${row.salesFactorMaxDiffPercentage ?? '-'} %`}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'firstUnhandledEventDate',
        headerName: t('myAssortment.priceControl.firstChangeDate'),
        renderCell: ({ row }) => getLLDate(row.firstUnhandledEventDate),
      },
      {
        ...baseColumnProps,
        width: 80,
        minWidth: 80,
        align: 'center',
        field: 'actions',
        header: '',
        renderCell: ({ row }) => (
          <IconButton
            sx={{
              borderRadius: 2,
              p: 1,
              border: (theme) => `1px solid ${theme.vars.palette.divider}`,
            }}
            onClick={() => onSupplierClick(row.number)}
          >
            <East fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [onSupplierClick, t]
  );

  return (
    <CustomDataGrid
      disableRowSelectionOnClick
      getRowClassName={getRowColorForStripedTable}
      getRowId={(row) => row.id}
      rowHeight={65}
      disableColumnMenu
      loading={isLoadingSuppliers}
      hideFooter
      rows={suppliersPriceControl}
      columns={columns}
      initialState={{
        pinnedColumns: { right: ['actions'] },
      }}
    />
  );
}
